/**
 * ContactListDetailPage.tsx
 *
 * Detail/edit page for a single contact list, reached via /marketing/contact-lists/:listId.
 * Handles both static lists (manual member management) and dynamic lists (filter builder + preview).
 *
 * Dynamic lists use a static sidebar with FilterControls (always expanded, no collapse/search)
 * plus ListPageLayout, with configs imported from the same shared files as each list page.
 */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Trash2, Save, Mail, Phone, Activity, Users, MoreVertical,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { FilterControls } from '@/components/filters/FilterControls';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { useToast } from '@/hooks/useToast';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { usePageContext } from '@/hooks/usePageContext';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { useLocalDashboardFilters } from '@/pages/dashboard/dashboardFilters';
import { cn } from '@/lib/utils';
import * as marketingSvc from '@/services/marketing.service';
import type { FilterValue } from '@/types/filters.types';
import {
  PreviewSection,
  buildFilterConditions,
  parseFilterConditions,
  AUTOMATION_STATUS_STYLES,
  SOURCE_TYPE_OPTIONS,
} from './ContactListShared';
import { getFilterConfigsForSource } from './contactListFilterConfigs';
import { useContactListFilterOptions } from './useContactListFilterOptions';

export default function ContactListDetailPage() {
  const { listId: listIdParam } = useParams<{ listId: string }>();
  const listId = Number(listIdParam);
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const updateAIContext = usePageContext('Contact List Detail');

  useEffect(() => {
    updateAIContext({ dataSummary: `Viewing contact list #${listId}` });
  }, [updateAIContext, listId]);

  const { data: list, isLoading } = useQuery({
    queryKey: ['marketing', 'list', listId],
    queryFn: () => marketingSvc.getAudienceList(listId),
    enabled: !Number.isNaN(listId),
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  const [sourceType, setSourceType] = useState('patient');
  const [liveMetrics, setLiveMetrics] = useState<{ total: number; uniqueEmailCount: number; uniquePhoneCount: number } | null>(null);

  const baselineRef = useRef<Record<string, unknown>>({});
  const [baselineReady, setBaselineReady] = useState(false);

  // Role titles for Plans filter labels
  const { roles } = useRoleTitles();
  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'MD';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';

  // Filter configs from the same shared source as each list page (exclude search-type filters)
  const filterConfigs = useMemo(
    () => getFilterConfigsForSource(sourceType, { doctorLabel, coordLabel })
      .filter((c) => c.type !== 'search'),
    [sourceType, doctorLabel, coordLabel],
  );

  // Filter state using the standard hook (no localStorage persistence)
  const filterState = useLocalDashboardFilters(filterConfigs);

  // Filter options from the same APIs as each list page
  const filterOptions = useContactListFilterOptions(sourceType);

  // Reset filters when source type changes
  const handleSourceTypeChange = useCallback((st: string) => {
    setSourceType(st);
    for (const key of Object.keys(filterState.values)) {
      filterState.clearFilter(key);
    }
  }, [filterState]);

  // Initialize from loaded list data — only hydrate filters that exist on the page
  const [listInitialized, setListInitialized] = useState(false);
  useEffect(() => {
    if (list && !listInitialized) {
      setName(list.name);
      setDescription(list.description ?? '');
      setActive(list.active);
      const lst = list as unknown as { sourceType?: string; filterConditions?: unknown };

      // DEBUG: show the exact filter data
      console.group('[DynamicList] Filter initialization for list #' + listId);
      const rawFC = (list as any).filterConditions;
      console.log('filterConditions AS STRING:', JSON.stringify(rawFC));
      console.log('filterConditions EXPANDED:', rawFC);
      console.log('filtersArray type:', rawFC ? typeof rawFC.filtersArray : 'NO filterConditions');
      console.log('filtersArray AS STRING:', rawFC ? JSON.stringify(rawFC.filtersArray) : 'N/A');

      const parsed = parseFilterConditions(lst.filterConditions);
      console.log('PARSED result:', parsed);

      const effectiveSource = parsed?.sourceType ?? lst.sourceType ?? sourceType;
      console.log('Effective source type:', effectiveSource);

      setSourceType(effectiveSource);
      if (parsed) {
        const validKeys = new Set(
          getFilterConfigsForSource(effectiveSource, { doctorLabel, coordLabel })
            .map((c) => c.key),
        );
        console.log('Valid filter keys for source:', [...validKeys]);
        console.log('Parsed filter keys:', Object.keys(parsed.filterValues));

        for (const [key, val] of Object.entries(parsed.filterValues)) {
          const accepted = validKeys.has(key);
          console.log(`  Filter "${key}":`, JSON.stringify(val), accepted ? '✓ ACCEPTED' : '✗ SKIPPED (not in valid keys)');
          if (accepted) {
            filterState.setFilter(key, val as FilterValue);
            if (parsed.negations[key]) filterState.setNegation(key, true);
          }
        }
      } else {
        console.warn('parseFilterConditions returned null — no filters to hydrate');
      }
      console.groupEnd();

      baselineRef.current = { name: list.name, description: list.description ?? '' };
      setListInitialized(true);
    }
  }, [list, listInitialized, filterState, sourceType, doctorLabel, coordLabel]);

  // Capture filter baseline after filter state has settled from init
  useEffect(() => {
    if (listInitialized && !baselineReady) {
      baselineRef.current = {
        ...baselineRef.current,
        filterValues: JSON.stringify(filterState.values),
        negations: JSON.stringify(filterState.negations),
      };
      setBaselineReady(true);
    }
  }, [listInitialized, baselineReady, filterState.values, filterState.negations]);

  // Build filter values — only include filters visible on the page
  const filterValues = useMemo(() => {
    const validKeys = new Set(filterConfigs.map((c) => c.key));
    const result: Record<string, string[] | { from: string; to: string }> = {};
    for (const [key, val] of Object.entries(filterState.values)) {
      if (!validKeys.has(key)) continue;
      if (Array.isArray(val) && val.length > 0) {
        result[key] = val;
      } else if (val && !Array.isArray(val) && 'from' in val && (val.from || val.to || (val as any).preset)) {
        result[key] = val as { from: string; to: string };
      }
    }
    return result;
  }, [filterState.values, filterConfigs]);

  const effectiveFilterConditions = useMemo(
    () => buildFilterConditions(sourceType, filterValues, filterState.negations),
    [sourceType, filterValues, filterState.negations],
  );

  // ── Unsaved changes ──
  const hasUnsavedChanges = useMemo(() => {
    if (!list || !baselineReady) return false;
    const b = baselineRef.current;
    if (name !== b.name) return true;
    if (description !== b.description) return true;
    if (list.listType === 'dynamic') {
      if (JSON.stringify(filterState.values) !== b.filterValues) return true;
      if (JSON.stringify(filterState.negations) !== b.negations) return true;
    }
    return false;
  }, [list, baselineReady, name, description, filterState.values, filterState.negations]);

  useUnsavedChangesGuard(hasUnsavedChanges);

  const handleBack = useCallback(() => {
    navigate('/marketing/contact-lists');
  }, [navigate]);

  // Mutations
  const updateMut = useMutation({
    mutationFn: (data: Parameters<typeof marketingSvc.updateAudienceList>[1]) =>
      marketingSvc.updateAudienceList(listId, data),
    onSuccess: (res) => {
      // The three counts only come back when the save carried filterConditions;
      // they arrive together, so one absent means none were recalculated and the
      // header keeps rendering the list's stored values.
      if (
        res.patientCount !== undefined &&
        res.uniqueEmailCount !== undefined &&
        res.uniquePhoneCount !== undefined
      ) {
        setLiveMetrics({
          total: res.patientCount,
          uniqueEmailCount: res.uniqueEmailCount,
          uniquePhoneCount: res.uniquePhoneCount,
        });
      }
      baselineRef.current = {
        name: name.trim(), description,
        filterValues: JSON.stringify(filterState.values),
        negations: JSON.stringify(filterState.negations),
      };
      qc.invalidateQueries({ queryKey: ['marketing', 'lists'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'list', listId] });
      toast.success('List saved');
    },
    onError: () => toast.error('Failed to save list'),
  });

  const deleteMut = useMutation({
    mutationFn: () => marketingSvc.deleteAudienceList(listId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'lists'] });
      toast.success('List deleted');
      navigate('/marketing/contact-lists');
    },
    onError: () => toast.error('Failed to delete list'),
  });
  const handleDelete = useCallback(() => {
    if (!window.confirm('Are you sure you want to delete this list? This action cannot be undone.')) return;
    deleteMut.mutate();
  }, [deleteMut]);

  useEffect(() => {
    if (!showOverflowMenu) return;
    const handler = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setShowOverflowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showOverflowMenu]);


  const handleSave = () => {
    if (!name.trim()) {
      toast.error('List name is required');
      return;
    }
    const payload: Parameters<typeof marketingSvc.updateAudienceList>[1] = {
      name: name.trim(),
      description,
      active,
    };
    if (list?.listType === 'dynamic') {
      payload.sourceType = sourceType;
      payload.filterConditions = effectiveFilterConditions;
    }
    updateMut.mutate(payload);
  };

  const handleFilterChange = useCallback((key: string, value: FilterValue) => {
    filterState.setFilter(key, value);
  }, [filterState]);

  if (isLoading || !list) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" label="Loading list..." />
      </div>
    );
  }

  const isDynamic = list.listType === 'dynamic';
  const hasLinkedAutomations = (list.linkedAutomations?.length ?? 0) > 0;
  const canDelete = !active && !hasLinkedAutomations;
  const canDeactivate = !hasLinkedAutomations;

  const handleToggleActive = () => {
    if (active && !canDeactivate) {
      toast.error('This list cannot be deactivated because it is used by an automation.');
      return;
    }
    const newActive = !active;
    setActive(newActive);
    updateMut.mutate({ active: newActive });
  };

  // Dynamic list: full-height sidebar layout
  if (isDynamic) {
    return (
      <>
        <ListPageLayout
          filterSidebar={(
            <div className="flex flex-col h-full w-64 border-r border-slate-200 bg-white">
              <div className="shrink-0 border-b border-slate-200 bg-blue-50 p-3">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Data Source</label>
                <Select
                  value={sourceType}
                  onChange={(v) => handleSourceTypeChange(v ?? 'patient')}
                  options={SOURCE_TYPE_OPTIONS}
                  disabled={!!list?.sourceType}
                />
                {list?.sourceType && (
                  <p className="text-[11px] text-slate-500 mt-1">Data source is locked after save</p>
                )}
              </div>
              <ScrollArea className="flex-1 min-h-0">
                <div className="p-4">
                  <FilterControls
                    filters={filterConfigs}
                    values={filterState.values}
                    options={filterOptions}
                    negations={filterState.negations}
                    onChange={handleFilterChange}
                    onNegateChange={filterState.setNegation}
                    onClear={filterState.clearFilter}
                    searchCommitMode="blur-or-enter"
                  />
                </div>
              </ScrollArea>
            </div>
          )}
        >
          <div className="flex h-full min-h-0 flex-col">
            <div className="shrink-0 p-5 space-y-4 border-b border-slate-200">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={handleBack} leftIcon={<ArrowLeft className="h-4 w-4" />}>
                    Back
                  </Button>
                  <h2 className="text-lg font-semibold text-slate-900">{list.name}</h2>
                  <Badge className="bg-[#fff3e0] text-[#e65100] ring-[#e65100]/20">Dynamic</Badge>
                  {hasUnsavedChanges && (
                    <div className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                      Unsaved changes
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="primary" leftIcon={<Save className="h-4 w-4" />} onClick={handleSave} loading={updateMut.isPending}>
                    Save Changes
                  </Button>
                  {canDelete && (
                    <div className="relative" ref={overflowRef}>
                      <button
                        type="button"
                        onClick={() => setShowOverflowMenu(p => !p)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-500 hover:bg-gray-50 transition-colors"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {showOverflowMenu && (
                        <div className="absolute right-0 z-50 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                          <button
                            type="button"
                            onClick={() => { setShowOverflowMenu(false); handleDelete(); }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete List
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats bar */}
              <div className="flex items-center gap-4">
                <span className="text-[28px] font-semibold text-[#00aff0]">{(liveMetrics?.total ?? list.patientCount ?? 0).toLocaleString()}</span>
                <span className="text-sm text-slate-500">contacts</span>
                <span className="inline-flex items-center gap-1 text-[13px] font-medium text-[#888] bg-[#f5f5f5] rounded-full px-2.5 py-1">
                  <Mail className="h-3.5 w-3.5 text-[#aaa]" fill="currentColor" stroke="white" strokeWidth={1.5} />
                  {(liveMetrics?.uniqueEmailCount ?? list.uniqueEmailCount ?? 0).toLocaleString()}
                </span>
                <span className="inline-flex items-center gap-1 text-[13px] font-medium text-[#888] bg-[#f5f5f5] rounded-full px-2.5 py-1">
                  <Phone className="h-3.5 w-3.5 text-[#aaa]" fill="currentColor" stroke="white" strokeWidth={1.5} />
                  {(liveMetrics?.uniquePhoneCount ?? list.uniquePhoneCount ?? 0).toLocaleString()}
                </span>
                <div className="border-l border-[#e5e5e5] pl-5 ml-3 flex items-center gap-2.5">
                  <label className="relative inline-block w-[50px] h-[26px] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={handleToggleActive}
                      className="sr-only peer"
                    />
                    <span className="absolute inset-0 rounded-full bg-[#ccc] transition-colors duration-300 peer-checked:bg-[#28a745]" />
                    <span className="absolute left-[3px] bottom-[3px] h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-transform duration-300 peer-checked:translate-x-6" />
                  </label>
                  <span className={cn('text-[13px] font-semibold min-w-[55px]', active ? 'text-[#28a745]' : 'text-[#999]')}>
                    {active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="ml-auto flex items-center gap-4 text-xs text-[#666]">
                  <span><strong className="text-[#333]">Created:</strong> {list.createdAt ? new Date(list.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                  <span><strong className="text-[#333]">Modified:</strong> {list.updatedAt ? new Date(list.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                </div>
              </div>

              {/* Automations link */}
              {hasLinkedAutomations && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Used by Automations</p>
                  <div className="flex flex-wrap gap-2">
                    {list.linkedAutomations.map((auto) => (
                      <span
                        key={`${auto.channel}-${auto.id}`}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-md text-sm font-medium text-slate-700"
                      >
                        <Activity className="h-3 w-3 text-slate-400" />
                        {auto.name}
                        <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-semibold', AUTOMATION_STATUS_STYLES[auto.status] || AUTOMATION_STATUS_STYLES.draft)}>
                          {auto.status}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Name / Description */}
              <div className="grid grid-cols-2 gap-4">
                <FormField label="List Name" required>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Active Members" />
                </FormField>
                <FormField label="Description">
                  <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the purpose of this list" />
                </FormField>
              </div>
            </div>

            {/* Preview — fills remaining viewport height */}
            <div className="flex-1 min-h-0 flex flex-col p-5">
              <PreviewSection
                sourceType={sourceType}
                filterConditions={effectiveFilterConditions}
                onMetricsChange={setLiveMetrics}
              />
            </div>
          </div>
        </ListPageLayout>

      </>
    );
  }

  // Static list: settings only (contacts are on a separate page)
  return (
    <div className="p-5 h-full flex flex-col">
      <div className="flex items-center justify-between pb-4 border-b border-slate-200 mb-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleBack} leftIcon={<ArrowLeft className="h-4 w-4" />}>
            Back
          </Button>
          <h2 className="text-lg font-semibold text-slate-900">{list.name}</h2>
          <Badge className="bg-[#e3f2fd] text-[#1565c0] ring-[#1565c0]/20">Static</Badge>
          {hasUnsavedChanges && (
            <div className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
              Unsaved changes
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            leftIcon={<Users className="h-4 w-4" />}
            onClick={() => navigate(`/marketing/contact-lists/${listId}`)}
          >
            View Contacts
          </Button>
          <Button variant="primary" leftIcon={<Save className="h-4 w-4" />} onClick={handleSave} loading={updateMut.isPending}>
            Save Changes
          </Button>
          {canDelete && (
            <div className="relative" ref={overflowRef}>
              <button
                type="button"
                onClick={() => setShowOverflowMenu(p => !p)}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {showOverflowMenu && (
                <div className="absolute right-0 z-50 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => { setShowOverflowMenu(false); handleDelete(); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete List
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 pb-4 border-b border-slate-200 mb-4 flex-shrink-0">
        <span className="text-[28px] font-semibold text-[#00aff0]">{(list.patientCount ?? 0).toLocaleString()}</span>
        <span className="text-sm text-slate-500">contacts</span>
        <span className="inline-flex items-center gap-1 text-[13px] font-medium text-[#888] bg-[#f5f5f5] rounded-full px-2.5 py-1">
          <Mail className="h-3.5 w-3.5 text-[#aaa]" fill="currentColor" stroke="white" strokeWidth={1.5} />
          {(list.uniqueEmailCount ?? 0).toLocaleString()}
        </span>
        <span className="inline-flex items-center gap-1 text-[13px] font-medium text-[#888] bg-[#f5f5f5] rounded-full px-2.5 py-1">
          <Phone className="h-3.5 w-3.5 text-[#aaa]" fill="currentColor" stroke="white" strokeWidth={1.5} />
          {(list.uniquePhoneCount ?? 0).toLocaleString()}
        </span>
        <div className="border-l border-[#e5e5e5] pl-5 ml-3 flex items-center gap-2.5">
          <label className="relative inline-block w-[50px] h-[26px] cursor-pointer">
            <input
              type="checkbox"
              checked={active}
              onChange={handleToggleActive}
              className="sr-only peer"
            />
            <span className="absolute inset-0 rounded-full bg-[#ccc] transition-colors duration-300 peer-checked:bg-[#28a745]" />
            <span className="absolute left-[3px] bottom-[3px] h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-transform duration-300 peer-checked:translate-x-6" />
          </label>
          <span className={cn('text-[13px] font-semibold min-w-[55px]', active ? 'text-[#28a745]' : 'text-[#999]')}>
            {active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-4 text-xs text-[#666]">
          <span><strong className="text-[#333]">Created:</strong> {list.createdAt ? new Date(list.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
          <span><strong className="text-[#333]">Modified:</strong> {list.updatedAt ? new Date(list.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
        </div>
      </div>

      {hasLinkedAutomations && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 flex-shrink-0">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Used by Automations</p>
          <div className="flex flex-wrap gap-2">
            {list.linkedAutomations.map((auto) => (
              <span
                key={`${auto.channel}-${auto.id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-md text-sm font-medium text-slate-700"
              >
                <Activity className="h-3 w-3 text-slate-400" />
                {auto.name}
                <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-semibold', AUTOMATION_STATUS_STYLES[auto.status] || AUTOMATION_STATUS_STYLES.draft)}>
                  {auto.status}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-6 flex-1 overflow-auto">
        <Card>
          <CardBody>
            <div className="space-y-4">
              <FormField label="List Name" required>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Newsletter Subscribers" />
              </FormField>
              <FormField label="Description">
                <Textarea autoResize value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Brief description of this list's purpose" />
              </FormField>
            </div>
          </CardBody>
        </Card>
      </div>

    </div>
  );
}
